import { Box, Divider, Grid, Typography } from '@mui/material'
import { NextRouter } from 'next/router'

import { Address } from '@ecm/data/Address'
import { Plan } from '@ecm/data/Plan'
import { selectPlan } from '@ecm/pages/pricing'
import Anchor from '@ecm/ui/element/Anchor'
import { Button } from '@ecm/ui/element/Button'
import Color from '@ecm/ui/element/Color'
import List from '@ecm/ui/element/List'
import MainContainer from '@ecm/ui/element/MainContainer'

type PlanBlockProps = {
  plans: Plan[]
  preSelectedAddress?: Address
  router: NextRouter
  bottomBanner?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const planIncludesList: any = {
  'high-volume-a': [
    <>
      <b>1 business address</b> of your choice
    </>,
    <>
      <b>Unlimited recipients</b>
    </>,
    <>
      <b>100 mail items</b>/month
    </>,
  ],
  'high-volume-b': [
    <>
      <b>1 business address</b> of your choice
    </>,
    <>
      <b>Unlimited recipients</b>
    </>,
    <>
      <b>250 mail items</b>/month
    </>,
  ],
  'lz-mailbox-a': [
    <>
      <b>1 business address</b> of your choice
    </>,
    <>
      <b>2 recipients</b> (e.g. you and your business)
    </>,
    <>
      <b>5 mail items</b>/month
    </>,
  ],
  'lz-mailbox-b': [
    <>
      <b>1 business address</b> of your choice
    </>,
    <>
      <b>5 recipients</b> (e.g. you, your business, and your business partners)
    </>,
    <>
      <b>25 mail items</b>/month
    </>,
  ],
  'mailbox-c': [
    <>
      <b>1 business address</b> of your choice
    </>,
    <>
      <b>20 recipients</b> (e.g. you, your business, and your business partners)
    </>,
    <>
      <b>50 mail items</b>/month
    </>,
  ],
}

const BottomBanner = () => (
  <Box
    sx={{
      backgroundColor: Color.smoke.normal,
      borderRadius: '8px',
      marginBottom: { md: '0', xs: '80px' },
      marginLeft: { lg: '32px', md: '16px', xs: '0' },
      marginTop: { lg: '40px', md: '80px', xs: '0' },
      padding: '16px 24px',
      textAlign: 'center',
      width: '100%',
    }}
  >
    <Anchor href="/pricing/detail" caret>
      See detailed pricing
    </Anchor>
  </Box>
)

const PlansBlock = ({ plans, preSelectedAddress, router, bottomBanner }: PlanBlockProps) => {
  return (
    <MainContainer
      sx={{
        alignItems: 'center',
        bgcolor: Color.white.normal,
        display: 'flex',
        flexDirection: 'column',
        paddingY: { md: '4em', xs: '2em' },
      }}
    >
      <Typography variant="h4" component="h2" mb="40px">
        Plans to fit your needs
      </Typography>

      <Grid container spacing={{ lg: 4, md: 2 }} justifyContent="center">
        {plans.map((plan: Plan) => (
          <Grid key={plan.handle} item xs={12} md={plan.businessType === 'highVolume' ? 6 : 4}>
            <Grid
              container
              direction="column"
              sx={{
                borderRadius: '20px',
                boxShadow: '2px 1px 7px rgba(0, 0, 0, 0.25)',
                boxSizing: 'border-box',
                marginBottom: '3em',
                textAlign: 'left',
              }}
            >
              <Box
                sx={{
                  backgroundColor: Color.smoke.normal,
                  borderRadius: '20px 20px 0 0',
                  height: '140px',
                  padding: '24px',
                }}
              >
                <Typography variant="h5" component="h3" mb="0">
                  {plan.name}
                </Typography>
                <Typography variant="p3" color={Color.saint.darkest}>
                  {plan.label}
                </Typography>
              </Box>

              <Grid container direction="column" p="24px">
                <Typography variant="h4" component="h3" mb="0">
                  ${plan.price}
                  <Typography variant="p3" component="span">
                    /mo
                  </Typography>
                </Typography>
                <Typography variant="p3" mb="24px">
                  auto-renews monthly
                </Typography>

                <Button
                  id={plan.handle}
                  onClick={() => selectPlan(router, plan, preSelectedAddress)}
                  primary
                  borderRadius="26px"
                >
                  Select plan
                </Button>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={{ lg: 4, md: 2 }} justifyContent="center">
        {plans.map((plan: Plan) => (
          <Grid
            key={plan.handle}
            item
            xs={12}
            md={plan.businessType === 'highVolume' ? 6 : 4}
            mb="2em"
          >
            {planIncludesList[plan.handle] && (
              <>
                <Typography variant="h6" mb="1em">
                  {plan.name} includes:
                </Typography>

                <Divider sx={{ borderColor: Color.smoke.darker, marginBottom: '24px' }} />

                <List
                  items={planIncludesList[plan.handle]}
                  icon="/images/icon/checkmark.svg"
                  variant="p3"
                  justifyContent="space-between"
                  height={{ md: '130px', xs: 'auto' }}
                  sx={{
                    '& li': {
                      color: Color.slate.darkest,
                      marginBottom: '2em',
                    },
                    '& li:before': {
                      backgroundSize: '23px',
                      left: '-40px',
                      opacity: 0.6,
                      top: '12px',
                    },
                  }}
                />
              </>
            )}
          </Grid>
        ))}
        {bottomBanner && <BottomBanner />}
      </Grid>
    </MainContainer>
  )
}

export default PlansBlock
